import React from 'react';
import useGoBackOrGoHome from './redirects/useGoBackOrGoHome';
import { SuspenseWrapper } from './SuspenseWrapper';
import { getCookieConsentData } from '../consent/getCookieConsentData';
import { useAppFeaturesContext } from '../contexts/AppFeaturesContext/AppFeaturesContext';
import { CookieConsentData } from '../consent/types';
import { useAuthContext } from '../contexts/AuthContext/AuthContext';
import { useNotificationContext } from '../contexts/NotificationContext/NotificationContext';
import { useNavigateWithHistory } from './useNavigateWithHistory';
import { ConsentAction, MutableConsentName } from '../appTypings';
import { acceptConsent } from '../consent/acceptConsent';
import {
  allConsentAcceptedNotification,
  selectedConsentAcceptedNotification,
} from '../CookieConsent/notifications';
import { logger } from '../../common/logger';
import { HOME_PATH } from './routeNames';

const ConsentRoute: React.FC = () => {
  const goBackOrGoHomeRef = React.useRef(useGoBackOrGoHome());
  const { languageCode } = useAppFeaturesContext();

  const [data, setData] = React.useState<CookieConsentData | null>(null);
  const [waitTarget, setWaitTarget] = React.useState<
    Promise<void> | undefined
  >();

  React.useEffect(() => {
    setWaitTarget(
      getCookieConsentData(languageCode, goBackOrGoHomeRef.current).then(data =>
        setData(data)
      )
    );
  }, [languageCode]);

  const { user } = useAuthContext();
  const { pushNotification } = useNotificationContext();
  const navigate = useNavigateWithHistory();

  const acceptAction = (
    acceptType: ConsentAction,
    consentSelections: Partial<Record<MutableConsentName, boolean>>
  ) =>
    void acceptConsent(acceptType, consentSelections, !!user)
      .then(() => {
        switch (acceptType) {
          case ConsentAction.ACCEPT_NECESSARY:
          case ConsentAction.ACCEPT_SELECTED:
            pushNotification(selectedConsentAcceptedNotification(languageCode));
            break;
          case ConsentAction.ACCEPT_ALL:
            pushNotification(allConsentAcceptedNotification(languageCode));
            break;
        }
      })
      .catch(() => logger.debug('consent action failed'))
      .finally(() => navigate(`/${HOME_PATH}`, { replace: true }));

  return (
    <SuspenseWrapper promiseToWait={waitTarget}>
      <AuthRequired>
        {data && <ConsentComponent {...data} acceptAction={acceptAction} />}
      </AuthRequired>
    </SuspenseWrapper>
  );
};

const ConsentComponent = React.lazy(
  () => import('../CookieConsent/CookieConsent')
);
const AuthRequired = React.lazy(() => import('./redirects/AuthRedirect'));

export default ConsentRoute;
